import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuLockKeyhole } from 'react-icons/lu';
import { useSession } from '../../context/SessionContext.jsx';
import { chargerReturnState } from '../../utils/chargerNav.js';
import {
  clearSessionPersistence,
  requestGuestSessionOtp,
  verifyGuestSessionOtp,
} from '../../api/sessionsApi.js';
import { clearActiveSession } from '../../utils/sessionPersistence.js';
import OtpInput from '../Common/OtpInput.jsx';
import './AuthRequiredView.less';

/**
 * Terminal "we can't verify this session" state that renders inside
 * the charging shell (App.jsx swaps it in whenever
 * `session.state.authFailed` is true).
 *
 * Because the previously rendered step unmounts, its polling
 * intervals / timeouts / SSE all clear via the normal effect
 * cleanups — no per-loop guard needed.
 *
 * Two exits:
 *
 *   1. "Recover with a code" — the backend emails a one-time code to
 *      the address already on this session's receipt (the driver types
 *      no email); the driver enters it in six boxes; on success the
 *      backend rotates the recovery cookie and we clear the authFailed
 *      flag so the shell reloads the original session without creating
 *      a new one.
 *   2. "Start again" — everything is wiped and we return the user
 *      to /charge for a fresh scan.
 */
// Safe, driver-facing wording for each failure. The backend logs the real
// cause (session, request id, stage, category); none of it is shown here.
const RECOVERY_MESSAGES = {
  RECOVERY_RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',
  RECOVERY_DELIVERY_FAILED: "We couldn't send the code right now. Please try again.",
  RECOVERY_FAILED: 'That code is incorrect or has expired. Check the latest email, or send a new code.',
  NETWORK_ERROR: "We couldn't reach GreenCharge. Check your connection and try again.",
};
const RECOVERY_UNAVAILABLE_MESSAGE =
  "We can't send a recovery code for this session. Please start again or contact support.";
function recoveryMessage(res, fallback) {
  if (res?.error === 'RECOVERY_UNAVAILABLE') return RECOVERY_UNAVAILABLE_MESSAGE;
  return RECOVERY_MESSAGES[res?.error] || fallback;
}
// " — you can request a new one in about 40 seconds", or "" if unknown/past.
function cooldownHint(resendAvailableAt) {
  const ms = resendAvailableAt ? new Date(resendAvailableAt).getTime() - Date.now() : 0;
  if (!(ms > 0)) return '';
  const secs = Math.ceil(ms / 1000);
  return ` — you can request a new one in about ${secs < 60 ? `${secs} seconds` : `${Math.ceil(secs / 60)} minutes`}`;
}

export default function AuthRequiredView() {
  const { state, update, reset, clearAuthFailed, rehydrateSession } = useSession();
  const navigate = useNavigate();

  const onStartAgain = useCallback(() => {
    try { clearSessionPersistence(); } catch { /* best-effort */ }
    try { clearActiveSession(); } catch { /* best-effort */ }
    clearAuthFailed();
    reset();
    navigate('/charge', {
      state: chargerReturnState({ returnTo: '/charge', source: 'user-dashboard' }),
    });
  }, [clearAuthFailed, reset, navigate]);

  // ── Code recovery state machine ────────────────────────────────
  // 'idle'      — the two exits
  // 'sending'   — POST /recovery/request in flight (first send or resend)
  // 'code'      — collecting the 6-digit code
  // 'verifying' — POST /recovery/verify in flight
  // 'resuming'  — verify succeeded, rehydrate in flight
  //
  // The driver never types an email: the server sends the code to the
  // address already on this session's receipt, and possession of the code
  // is the proof. Nothing about that address is shown here.
  const [phase, setPhase] = useState('idle');
  const [codeStep, setCodeStep] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const sessionUid = state?.sessionUid || null;
  const canRecover = !!sessionUid && !unavailable;

  const onRequestOtp = useCallback(async () => {
    const resend = codeStep;
    setError(null);
    setNotice(null);
    setPhase('sending');
    const res = await requestGuestSessionOtp(sessionUid);
    if (!res.ok) {
      if (res.error === 'RECOVERY_UNAVAILABLE') {
        setUnavailable(true);
        setCodeStep(false);
        setPhase('idle');
        setError(RECOVERY_UNAVAILABLE_MESSAGE);
        return;
      }
      setPhase(resend ? 'code' : 'idle');
      setError(recoveryMessage(res, 'Could not send the recovery code. Please try again.'));
      return;
    }
    setCodeStep(true);
    setPhase('code');
    if (res.data?.sent === false) {
      // A code went out moments ago (resend cooldown): that one still works.
      setNotice(`We sent a code to the email on your receipt moments ago. Check your inbox${
        cooldownHint(res.data?.resendAvailableAt)}.`);
    } else {
      setNotice(resend
        ? 'We sent a new 6-digit code to the email on your receipt. Check your inbox.'
        : 'We sent a 6-digit code to the email on your receipt. Check your inbox.');
    }
  }, [sessionUid, codeStep]);

  const onVerifyOtp = useCallback(async () => {
    setError(null);
    setNotice(null);
    if (!/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit code from the email.');
      return;
    }
    setPhase('verifying');
    const res = await verifyGuestSessionOtp(sessionUid, { code: otp });
    if (!res.ok) {
      setPhase('code');
      setError(recoveryMessage(res, "We couldn't check the code right now. Please try again."));
      return;
    }
    setPhase('resuming');
    // Recovery cookie is set by the server. Clear the auth-failed flag
    // and rehydrate so the shell picks the session state up cleanly —
    // NO new session is created; the original sessionUid is resumed.
    // Also drop a start failure an older build recorded from the same 401
    // (it is persisted): it would show "Session expired" over the session
    // just recovered. Rehydrate still resets a genuinely ended session.
    clearAuthFailed();
    update({ sessionStartFailed: false });
    try {
      await rehydrateSession(sessionUid);
    } catch { /* rehydrate will report via its own state */ }
    setPhase('idle');
  }, [sessionUid, otp, update, clearAuthFailed, rehydrateSession]);

  const onCancelRecovery = useCallback(() => {
    setPhase('idle');
    setCodeStep(false);
    setOtp('');
    setError(null);
    setNotice(null);
  }, []);

  const busy = phase === 'sending' || phase === 'verifying' || phase === 'resuming';
  const showCode = codeStep && phase !== 'resuming' && phase !== 'idle';

  return (
    <div className="gc-auth-required" role="alert">
      <div className="gc-auth-required-card">
        <div className="gc-auth-required-icon" aria-hidden="true">
          <LuLockKeyhole />
        </div>
        <h1 className="gc-auth-required-title">
          We couldn't verify this session
        </h1>
        <p className="gc-auth-required-body">
          Your charging session is still on our records, but this browser
          can no longer prove ownership of it. Recover with a code sent
          to the email on the receipt, or start a new session.
        </p>
        {sessionUid && (
          <p className="gc-auth-required-ref">
            Reference: <code>{sessionUid}</code>
          </p>
        )}

        {!showCode && phase !== 'resuming' && (
          <div className="gc-auth-required-actions">
            {canRecover && (
              <button
                type="button"
                className="gc-auth-required-cta gc-auth-required-cta-primary"
                onClick={onRequestOtp}
                disabled={busy}
              >
                {phase === 'sending' ? 'Sending code…' : 'Recover with a code'}
              </button>
            )}
            <button
              type="button"
              className="gc-auth-required-cta"
              onClick={onStartAgain}
              disabled={busy}
            >
              Start again
            </button>
          </div>
        )}

        {showCode && (
          <div className="gc-auth-required-recovery">
            {/* Six boxes, one digit each (the shared OtpInput: paste and
                iOS one-time-code autofill spread across the boxes). No
                auto-submit: "Recover session" submits. After a rejected
                code the boxes keep it and focus returns to the first box,
                selected, ready to overtype. */}
            <OtpInput
              name="recovery-code"
              label="Enter the 6-digit code"
              digitLabel="Recovery code digit"
              value={otp}
              onChange={setOtp}
              disabled={busy}
              invalid={!!error && phase === 'code'}
              autoFocus
            />
            <div className="gc-auth-required-buttonrow">
              <button
                type="button"
                className="gc-auth-required-cta gc-auth-required-cta-primary"
                onClick={onVerifyOtp}
                disabled={busy}
              >
                {phase === 'verifying' ? 'Verifying…' : 'Recover session'}
              </button>
              <button
                type="button"
                className="gc-auth-required-cta"
                onClick={onRequestOtp}
                disabled={busy}
              >
                {phase === 'sending' ? 'Sending…' : 'Resend code'}
              </button>
              <button
                type="button"
                className="gc-auth-required-cta gc-auth-required-cta-ghost"
                onClick={onCancelRecovery}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {phase === 'resuming' && (
          <div className="gc-auth-required-recovery">
            <p className="gc-auth-required-body">Resuming your session…</p>
          </div>
        )}

        {error && (
          <p className="gc-auth-required-error" role="alert">{error}</p>
        )}
        {notice && (
          <p className="gc-auth-required-notice" role="status">{notice}</p>
        )}
      </div>
    </div>
  );
}
